import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { AccesAdminProvider, type AccesAdmin } from './acces';
import { adminFetch } from './adminFetch';
import './admin.css';

type Etat =
  | { etape: 'chargement' }
  | { etape: 'pret'; acces: AccesAdmin }
  | { etape: 'refus'; titre: string; message: string; reconnexion: boolean };

/**
 * Back-office de la SUITE, dans le portail (27/09/2026).
 *
 * Réservé aux superadmins et aux agents support, sur une session ouverte par
 * mot de passe + TOTP. Tout ce qui est COMMUN aux produits s'administre ici :
 * tickets, équipe de la suite, et depuis le 27/09/2026 le catalogue commercial
 * (formules, fonctionnalités, codes), les produits affichés et l'ouverture
 * (inscriptions, vente). Les back-offices des produits ne gardent que ce qui
 * leur est propre. Cette page ne protège rien : c'est `AdminGuard`, côté
 * API, qui refuse. Elle rend seulement le refus lisible, et dit quoi faire.
 */
export function AdminLayout() {
  const navigate = useNavigate();
  const [etat, setEtat] = useState<Etat>({ etape: 'chargement' });

  useEffect(() => {
    let annule = false;
    void (async () => {
      const res = await adminFetch('/admin/moi').catch(() => null);
      if (annule) return;
      if (!res) {
        setEtat({ etape: 'refus', titre: 'Portail injoignable', message: 'Réessayez dans un instant.', reconnexion: false });
        return;
      }
      if (res.status === 401) {
        navigate('/login');
        return;
      }
      if (res.ok) {
        setEtat({ etape: 'pret', acces: (await res.json()) as AccesAdmin });
        return;
      }
      const corps = (await res.json().catch(() => ({}))) as { error?: string };
      if (corps.error === 'admin_amr_required') {
        setEtat({
          etape: 'refus',
          titre: 'Double authentification requise',
          message:
            'Le back-office exige une session ouverte avec votre mot de passe ET votre code TOTP. Déconnectez-vous puis reconnectez-vous ainsi.',
          reconnexion: true,
        });
        return;
      }
      // « Accès réservé » seulement si l'API l'a DIT. Tout autre échec (404 d'une
      // API pas encore redéployée, 5xx…) accusait à tort le compte : c'est ce
      // qui s'est produit en préprod le 28/09/2026, le portail étant à jour et
      // l'API non.
      if (corps.error === 'superadmin_required' || corps.error === 'support_required') {
        setEtat({
          etape: 'refus',
          titre: 'Accès réservé',
          message: 'Ce back-office est réservé aux superadmins et aux agents support de la suite SEmply.',
          reconnexion: false,
        });
        return;
      }
      setEtat({
        etape: 'refus',
        titre: 'Back-office indisponible',
        message:
          res.status === 404
            ? "L'API du portail ne connaît pas encore le back-office (erreur 404) : elle n'est sans doute pas à jour sur cet environnement."
            : `L'API du portail a répondu une erreur ${res.status}. Réessayez dans un instant.`,
        reconnexion: false,
      });
    })();
    return () => {
      annule = true;
    };
  }, [navigate]);

  const deconnecter = () => void api.post('/auth/logout').finally(() => navigate('/login'));

  if (etat.etape === 'chargement') {
    return <main className="adm-center">Chargement…</main>;
  }
  if (etat.etape === 'refus') {
    return (
      <main className="adm-center">
        <div className="adm-refus">
          <h1>{etat.titre}</h1>
          <p>{etat.message}</p>
          <div className="adm-refus__actions">
            {etat.reconnexion ? (
              <button className="btn" type="button" onClick={deconnecter}>
                Se déconnecter
              </button>
            ) : (
              <Link className="btn btn-secondary" to="/compte">
                Mon compte
              </Link>
            )}
          </div>
        </div>
      </main>
    );
  }

  const { acces } = etat;
  return (
    <AccesAdminProvider value={acces}>
      <div className="adm-shell">
        <header className="adm-header">
          <div className="adm-brand">
            <strong>SEmply</strong>
            <span>Back-office de la suite</span>
          </div>
          <div className="adm-session">
            <span>
              {acces.email} — {acces.superadmin ? 'Superadmin' : 'Agent support'}
            </span>
            <Link to="/compte">Mon compte</Link>
            <button type="button" className="adm-link" onClick={deconnecter}>
              Se déconnecter
            </button>
          </div>
        </header>
        <nav className="adm-nav" aria-label="Administration">
          <NavLink to="/admin/tickets">Tickets</NavLink>
          {acces.superadmin && (
            <>
              <NavLink to="/admin/catalogue">Catalogue</NavLink>
              <NavLink to="/admin/produits">Produits</NavLink>
              <NavLink to="/admin/ouverture">Ouverture</NavLink>
              <NavLink to="/admin/beta">Bêta</NavLink>
              <NavLink to="/admin/questions-dossier">Questionnaire</NavLink>
              <NavLink to="/admin/referentiels">Référentiels</NavLink>
              <NavLink to="/admin/comptes">Comptes</NavLink>
              <NavLink to="/admin/equipe">Équipe</NavLink>
            </>
          )}
        </nav>
        <main className="adm-main">
          <Outlet />
        </main>
      </div>
    </AccesAdminProvider>
  );
}
